import React, { useState, useEffect } from 'react';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import ProcessingStatusBadge from '../../components/ui/ProcessingStatusBadge';
import OverallProgressDashboard from './components/OverallProgressDashboard';
import ProcessingStageTimeline from './components/ProcessingStageTimeline';
import LiveActivityFeed from './components/LiveActivityFeed';
import ProcessingSummaryStats from './components/ProcessingSummaryStats';
import ProcessingControlPanel from './components/ProcessingControlPanel';

const ProcessingStatusMonitor = () => {
  const [processingState, setProcessingState] = useState({
    isProcessing: true,
    isPaused: false,
    overallProgress: 67.5,
    currentStage: 2,
    estimatedTimeRemaining: 8.5
  });

  const [processingStages] = useState([
    {
      id: 'normalization',
      name: 'Data Normalization',
      type: 'normalization',
      description: 'Cleaning and standardizing product data, removing duplicates, and formatting fields',
      status: 'completed',
      progress: 100,
      itemsProcessed: 2847,
      totalItems: 2847,
      matchesFound: 0,
      processingSpeed: 450,
      estimatedTime: null
    },
    {
      id: 'exact-ean',
      name: 'Exact EAN Matching',
      type: 'exact-ean',
      description: 'Matching products using exact EAN/UPC codes for highest confidence matches',
      status: 'completed',
      progress: 100,
      itemsProcessed: 2847,
      totalItems: 2847,
      matchesFound: 1243,
      processingSpeed: 380,
      estimatedTime: null
    },
    {
      id: 'brand-mpn',
      name: 'Brand + MPN Matching',
      type: 'brand-mpn',
      description: 'Matching products using exact brand and manufacturer part number combinations',
      status: 'processing',
      progress: 73.2,
      itemsProcessed: 1174,
      totalItems: 1604,
      matchesFound: 487,
      processingSpeed: 125,
      estimatedTime: '3.4m',
      currentOperation: 'Processing Samsung Galaxy products...'
    },
    {
      id: 'fuzzy-matching',
      name: 'AI Fuzzy Matching',
      type: 'fuzzy-matching',
      description: 'Using AI algorithms to find similar products based on descriptions and features',
      status: 'pending',
      progress: 0,
      itemsProcessed: 0,
      totalItems: 1117,
      matchesFound: 0,
      processingSpeed: null,
      estimatedTime: '5.1m'
    }
  ]);

  const [liveActivities, setLiveActivities] = useState([
    {
      id: 1,
      type: 'fuzzy_match',
      productName: 'Samsung Galaxy S23 Ultra 256GB',
      matchedProduct: 'Samsung Galaxy S23 Ultra 256GB Phantom Black',
      confidence: 0.94,
      supplier: 'TechDistributor',
      price: 1199.99,
      timestamp: Date.now() - 2000,
      details: 'Matched via description similarity and brand recognition'
    },
    {
      id: 2,
      type: 'exact_match',
      productName: 'Apple iPhone 14 Pro 128GB',
      matchMethod: 'EAN',
      confidence: 1.0,
      supplier: 'MobileWorld',
      price: 999.99,
      timestamp: Date.now() - 5000
    },
    {
      id: 3,
      type: 'ambiguous',
      productName: 'Sony WH-1000XM4 Headphones',
      confidence: 0.87,
      supplier: 'AudioPlus',
      price: 349.99,
      timestamp: Date.now() - 8000,
      details: 'Multiple potential matches found - requires manual review'
    },
    {
      id: 4,
      type: 'exact_match',
      productName: 'Dell XPS 13 Laptop i7 16GB',
      matchMethod: 'MPN',
      confidence: 1.0,
      supplier: 'ComputerHub',
      price: 1299.99,
      timestamp: Date.now() - 12000
    },
    {
      id: 5,
      type: 'no_match',
      productName: 'Generic USB-C Cable 3ft',
      supplier: 'CableStore',
      price: 12.99,
      timestamp: Date.now() - 15000,
      details: 'No matching products found in database'
    }
  ]);

  const [summaryStats] = useState({
    totalProcessed: 2021,
    exactMatches: 1243,
    fuzzyMatches: 487,
    ambiguousMatches: 156,
    noMatches: 135,
    processingSpeed: 125,
    memoryUsage: 68,
    cpuUsage: 45,
    queueSize: 826,
    estimatedTimeRemaining: '8m',
    highConfidenceMatches: 1730,
    mediumConfidenceMatches: 487,
    lowConfidenceMatches: 156,
    veryLowConfidenceMatches: 48
  });

  // Simulate live activity updates
  useEffect(() => {
    if (!processingState?.isProcessing || processingState?.isPaused) return;

    const interval = setInterval(() => {
      const newActivity = {
        id: Date.now(),
        type: ['exact_match', 'fuzzy_match', 'ambiguous', 'no_match']?.[Math.floor(Math.random() * 4)],
        productName: [
          'Logitech MX Master 3 Mouse',
          'Microsoft Surface Pro 9',
          'Canon EOS R5 Camera',
          'Nintendo Switch OLED',
          'Bose QuietComfort Earbuds'
        ]?.[Math.floor(Math.random() * 5)],
        confidence: 0.7 + Math.random() * 0.3,
        supplier: ['TechWorld', 'GadgetHub', 'ElectroStore', 'DeviceCenter']?.[Math.floor(Math.random() * 4)],
        price: Math.floor(Math.random() * 1000) + 50,
        timestamp: Date.now()
      };

      setLiveActivities(prev => [newActivity, ...prev?.slice(0, 9)]);
    }, 3000);

    return () => clearInterval(interval);
  }, [processingState?.isProcessing, processingState?.isPaused]);

  const handlePause = () => {
    setProcessingState(prev => ({ ...prev, isPaused: true }));
  };

  const handleResume = () => {
    setProcessingState(prev => ({ ...prev, isPaused: false }));
  };

  const handleCancel = () => {
    setProcessingState(prev => ({ 
      ...prev, 
      isProcessing: false, 
      isPaused: false 
    }));
    // Redirect to file upload after cancellation
    setTimeout(() => {
      window.location.href = '/file-upload-dashboard';
    }, 1000);
  };

  const handleViewResults = () => {
    window.location.href = '/results-dashboard';
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Processing Status Monitor</h1>
                <p className="text-muted-foreground mt-2">
                  Real-time monitoring of AI-powered product matching operations
                </p>
              </div>
              
              <div className="flex items-center space-x-4">
                <ProcessingStatusBadge 
                  status={processingState?.isProcessing ? (processingState?.isPaused ? 'paused' : 'processing') : 'completed'}
                  progress={processingState?.overallProgress}
                  message={processingState?.isPaused ? 'Processing paused' : 'AI matching in progress'}
                />
              </div>
            </div>
          </div>

          {/* Workflow Progress */}
          <div className="mb-8">
            <WorkflowProgressIndicator 
              currentStep={2}
              completedSteps={['upload', 'configure']}
              processingStep="process"
            />
          </div>

          {/* Overall Progress Dashboard */}
          <OverallProgressDashboard 
            overallProgress={processingState?.overallProgress}
            estimatedTimeRemaining={processingState?.estimatedTimeRemaining}
            currentStage={processingState?.currentStage}
            totalStages={4}
          />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Left Column - Processing Timeline */}
            <div className="xl:col-span-2 space-y-8">
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-xl font-semibold text-foreground mb-6">Processing Stages</h2>
                <ProcessingStageTimeline 
                  stages={processingStages}
                  currentStage={processingState?.currentStage}
                />
              </div>

              {/* Live Activity Feed - Mobile/Tablet */}
              <div className="xl:hidden">
                <LiveActivityFeed 
                  activities={liveActivities}
                  isProcessing={processingState?.isProcessing && !processingState?.isPaused}
                />
              </div>
            </div>

            {/* Right Column - Stats and Controls */}
            <div className="space-y-6">
              {/* Processing Controls */}
              <ProcessingControlPanel 
                isProcessing={processingState?.isProcessing}
                isPaused={processingState?.isPaused}
                canViewResults={processingState?.overallProgress > 25}
                onPause={handlePause}
                onResume={handleResume}
                onCancel={handleCancel}
                onViewResults={handleViewResults}
              />

              {/* Live Activity Feed - Desktop */}
              <div className="hidden xl:block">
                <LiveActivityFeed 
                  activities={liveActivities}
                  isProcessing={processingState?.isProcessing && !processingState?.isPaused}
                />
              </div>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="mt-8">
            <ProcessingSummaryStats stats={summaryStats} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProcessingStatusMonitor;